import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { ExportVideoButton } from "./ExportVideoButton";
import ExportOption from "./ExportOption";
import ExportQualityView from "./ExportQualityView";
import { Button } from "@/lib/components/Button/Button";
import { IconChevronRight, IconLoader2 } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import EarlierExportsView from "./EarlierExportsView";
import { useParams } from "react-router";
import {
  useCreateVideoRenderMutation,
  useVideoRendersForVideoQuery,
  videoRenderQueryKeys,
} from "@/lib/queries/video-render-queries";
import Toast from "@/lib/components/Toast";
import ExportDetailsView from "./ExportDetailsView";
import ShareLinkView from "./ShareLinkView";
import { queryClient } from "@/lib/queries/query-client";
import { VideoRenderExportType } from "@repo/api/video-render";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import {
  useVideoRendersDownloadWatcher,
  useVideoRendersFailedWatcher,
} from "./export-video-utils";

export type ExportVideoDetailsViewState = {
  view: "EXPORT_DETAILS";
  videoRenderExternalId: string;
  exportType: VideoRenderExportType;
  fromView: "EXPORT" | "EARLIER_EXPORTS";
};

export type ShareLinkViewState = {
  view: "SHARE_LINK";
  videoRenderExternalId?: string;
  fromView: "EARLIER_EXPORTS" | "EXPORT_QUALITY" | "EXPORT_DETAILS";
  exportType?: VideoRenderExportType;
  detailsFromView?: "EXPORT" | "EARLIER_EXPORTS";
};

export type ExportVideoDialogState =
  | {
      view: "EXPORT" | "EARLIER_EXPORTS";
    }
  | {
      view: "EXPORT_QUALITY";
      exportType: VideoRenderExportType;
    }
  | ExportVideoDetailsViewState
  | ShareLinkViewState;

export default function ExportVideoDialog() {
  const { teamExternalId } = useParams();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [dialogState, setDialogState] = useState<ExportVideoDialogState>({
    view: "EXPORT",
  });

  const video = useEditorStore((state) => state.video!);
  const refreshCreditBalance = useEditorStore(
    (state) => state.service.refreshCreditBalance,
  );
  const setIsCreditDialogOpen = useEditorStore(
    (state) => state.service.setIsCreditDialogOpen,
  );

  const { data: videoRenders } = useVideoRendersForVideoQuery(
    teamExternalId!,
    video.externalId,
  );

  const { setVideoRendersToDownload } =
    useVideoRendersDownloadWatcher(videoRenders);

  useVideoRendersFailedWatcher(videoRenders);

  const { mutate: createVideoRender, isPending: isCreatingVideoRender } =
    useCreateVideoRenderMutation({
      teamExternalId: teamExternalId!,
      videoExternalId: video.externalId,
      onSuccess: async (data, variables) => {
        if (variables.exportType === "DOWNLOAD") {
          setVideoRendersToDownload((prevState) => {
            const newState = new Set(prevState);
            newState.add(data.externalId);
            return newState;
          });
        }

        await queryClient.invalidateQueries({
          queryKey: videoRenderQueryKeys.getVideoRendersForVideo(
            teamExternalId!,
            video.externalId,
          ),
        });

        await refreshCreditBalance();

        if (variables.exportType === "LINK") {
          setDialogState({
            view: "SHARE_LINK",
            videoRenderExternalId: data.externalId,
            fromView: "EXPORT_QUALITY",
          });
          return;
        }

        setDialogState({
          view: "EXPORT_DETAILS",
          videoRenderExternalId: data.externalId,
          exportType: variables.exportType,
          fromView: "EXPORT",
        });
      },
      onError: async (error: any) => {
        if (error?.response?.data?.errorCode === "NOT_ENOUGH_CREDITS") {
          await refreshCreditBalance();
          setIsExportDialogOpen(false);
          setIsCreditDialogOpen(true);
          return;
        }

        console.error(error);
        Toast.error("Failed to export video");
      },
    });

  const isRendering =
    videoRenders?.some((render) =>
      ["rendering", "pending", "upscaling"].includes(render.status),
    ) ?? false;

  const handleOpenChange = useCallback((open: boolean) => {
    setIsExportDialogOpen(open);

    if (open) {
      setDialogState({ view: "EXPORT" });
    }

    if (!open) {
      AnalyticsApi.logExportVideoDialogClosed();
    }
  }, []);

  return (
    <Dialog open={isExportDialogOpen} onOpenChange={handleOpenChange}>
      <ExportVideoButton isRendering={isRendering} />
      <DialogContent
        className="min-h-110 max-w-2xl"
        classNameInner="flex flex-col gap-0 p-0 sm:px-0"
        showCloseButton={false}
      >
        {dialogState.view === "EXPORT" && (
          <ExportVideoDialogContent
            setDialogState={setDialogState}
            isRendering={isRendering}
          />
        )}
        {dialogState.view === "EXPORT_QUALITY" && (
          <ExportQualityView
            exportType={dialogState.exportType}
            createVideoRender={createVideoRender}
            isCreatingVideoRender={isCreatingVideoRender}
            videoName={video.name}
            setDialogState={setDialogState}
            videoRenders={videoRenders}
          />
        )}
        {dialogState.view === "EARLIER_EXPORTS" && (
          <EarlierExportsView
            setDialogState={setDialogState}
            videoRenders={videoRenders}
          />
        )}
        {dialogState.view === "EXPORT_DETAILS" && (
          <ExportDetailsView
            videoRenderExternalId={dialogState.videoRenderExternalId}
            dialogState={dialogState}
            setDialogState={setDialogState}
            videoRenders={videoRenders}
            exportType={dialogState.exportType}
            video={video}
          />
        )}
        {dialogState.view === "SHARE_LINK" && (
          <ShareLinkView
            dialogState={dialogState}
            setDialogState={setDialogState}
            videoRenders={videoRenders}
            teamExternalId={teamExternalId!}
            videoExternalId={video.externalId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

type ExportVideoDialogContentProps = {
  setDialogState: (state: ExportVideoDialogState) => void;
  isRendering: boolean;
};

function ExportVideoDialogContent({
  setDialogState,
  isRendering,
}: ExportVideoDialogContentProps) {
  return (
    <>
      <DialogHeader variant="bordered" showCloseButton={true}>
        <DialogTitle>Export video</DialogTitle>
        <DialogDescription className="sr-only">Export video</DialogDescription>
      </DialogHeader>
      <div className="flex h-full flex-1 flex-col">
        <div className="flex flex-wrap justify-center gap-6 px-4 pt-16 pb-10">
          <ExportOption
            type="DOWNLOAD"
            onSelect={(exportType) =>
              setDialogState({ view: "EXPORT_QUALITY", exportType })
            }
          />
          <ExportOption
            type="LINK"
            onSelect={(exportType) =>
              setDialogState({ view: "EXPORT_QUALITY", exportType })
            }
          />
          <ExportOption
            type="VIMEO"
            onSelect={(exportType) =>
              setDialogState({ view: "EXPORT_QUALITY", exportType })
            }
          />
        </div>
        <div className="flex flex-1 items-end justify-center pb-8">
          <Button
            variant="ghost"
            size="sm"
            icon={
              isRendering ? (
                <IconLoader2 className="animate-spin" />
              ) : (
                <IconChevronRight />
              )
            }
            onClick={() => setDialogState({ view: "EARLIER_EXPORTS" })}
          >
            Earlier exports
          </Button>
        </div>
      </div>
    </>
  );
}
